import { EIDPattern } from "constants/scanner";
import { formatEid, parseIso11784Data } from "lib/eid/iso11784";

import { EIDOutputFormat } from "./types";

const HEX_EID_PATTERN = /^[A-Fa-f0-9]{16}$/;
const ISO_23_STX = "\x02";
const ISO_23_BODY_PATTERN = /^A(\d{10})(\d{12})$/;
const ISO_TIMESTAMP_PATTERN =
  /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,3})?Z?/;

function convertHexEidToAscii(hex: string): string {
  const tagData = parseIso11784Data(BigInt(`0x${hex}`));
  return formatEid(tagData);
}

function parseIso23(candidate: string): string | null {
  // Linting prefers not to use control characters such as STX in regex
  if (!candidate.startsWith(ISO_23_STX)) {
    return null;
  }
  const match = ISO_23_BODY_PATTERN.exec(candidate.slice(1));
  if (!match) {
    return null;
  }
  const country = parseInt(match[1], 10);
  return `${country.toString().padStart(3, "0")} ${match[2]}`;
}

function parseWithSpace(candidate: string): string | null {
  const match = EIDPattern.SIXTEEN_DIGIT_PATTERN.exec(candidate);
  return match ? match[1] : null;
}

function parseNoSpace(candidate: string): string | null {
  return /^\d{15}$/.test(candidate)
    ? `${candidate.slice(0, 3)} ${candidate.slice(3)}`
    : null;
}

function parseHex(candidate: string): string | null {
  return HEX_EID_PATTERN.test(candidate)
    ? convertHexEidToAscii(candidate)
    : null;
}

function parseIso(candidate: string): string | null {
  const withTimestamp =
    EIDPattern.ISO_24631_W_TIMESTAMP_PATTERN.exec(candidate);
  if (withTimestamp) {
    return `${withTimestamp[1].padStart(3, "0")} ${withTimestamp[2].padStart(12, "0")}`;
  }
  const match = EIDPattern.ISO_24631_PATTERN.exec(candidate);
  return match
    ? `${match[1].padStart(3, "0")} ${match[2].padStart(12, "0")}`
    : null;
}

export function tryParseEidOutputFormat(
  payload: string,
): EIDOutputFormat | undefined {
  const raw = payload.trim().replace(/^\[/, "").replace(/]$/, "").trim();

  if (
    raw === EIDOutputFormat.WITH_SPACE ||
    raw === EIDOutputFormat.NO_SPACE ||
    raw === EIDOutputFormat.HEX ||
    raw === EIDOutputFormat.ISO ||
    raw === EIDOutputFormat.ISO_23
  ) {
    return raw;
  }

  return undefined;
}

export function normaliseEid(
  payload: string,
  outputFormat: EIDOutputFormat | undefined,
): string | null {
  const candidate = payload.trim();
  if (!candidate) {
    return null;
  }

  if (outputFormat === EIDOutputFormat.ISO_23) {
    return parseIso23(candidate);
  }
  if (outputFormat === EIDOutputFormat.ISO) {
    return parseIso(candidate);
  }
  if (outputFormat === EIDOutputFormat.WITH_SPACE) {
    return parseWithSpace(candidate);
  }
  if (outputFormat === EIDOutputFormat.NO_SPACE) {
    return parseNoSpace(candidate);
  }
  if (outputFormat === EIDOutputFormat.HEX) {
    return parseHex(candidate);
  }

  return (
    parseWithSpace(candidate) ?? parseNoSpace(candidate) ?? parseHex(candidate)
  );
}

export function extractEidFromRecord(
  record: string,
  outputFormat: EIDOutputFormat | undefined,
): string | null {
  const directMatch = normaliseEid(record, outputFormat);
  if (directMatch) {
    return directMatch;
  }

  for (const part of record
    .split(",")
    .map(token => token.trim())
    .filter(Boolean)) {
    const tokenMatch = normaliseEid(part, outputFormat);
    if (tokenMatch) {
      return tokenMatch;
    }
  }

  return null;
}

export function extractScanTime(record: string): string {
  const match = ISO_TIMESTAMP_PATTERN.exec(record);
  return match ? new Date(match[0]).toISOString() : new Date().toISOString();
}
